import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Loader2, TrendingUp, TrendingDown, Repeat, ArrowLeftRight, Lock } from "lucide-react";
import { useSubscription } from "@/hooks/use-subscription";
import { UpgradePrompt } from "@/components/subscription/UpgradePrompt";
import { format, addMonths, addWeeks, addYears } from "date-fns";
import { CategorySelect } from "@/components/shared/CategorySelect";

interface Transaction {
  id: string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  category_id: string | null;
  account_id: string;
  transaction_date: string;
  is_paid: boolean;
  is_recurring: boolean;
  is_fixed: boolean;
  category?: {
    id: string;
    name: string;
  } | null;
}

interface TransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  transaction?: Transaction | null;
  defaultAccountId?: string;
}

const TRANSFER_EXPENSE_CAT_ID = '00000000-0000-0000-0000-000000000101';
const TRANSFER_INCOME_CAT_ID = '00000000-0000-0000-0000-000000000102';

export const TransactionModal = ({ open, onOpenChange, onSuccess, transaction, defaultAccountId }: TransactionModalProps) => {
  const isEditing = !!transaction;
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<"income" | "expense" | "transfer">("expense");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [destinationAccountId, setDestinationAccountId] = useState("");
  const [transferPairId, setTransferPairId] = useState<string | null>(null);
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Recurrence fields
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<"installment" | "fixed">("installment");
  const [installments, setInstallments] = useState("2");
  const [frequency, setFrequency] = useState<"weekly" | "monthly" | "yearly">("monthly");
  
  const [accounts, setAccounts] = useState<any[]>([]);
  const [showTransferUpgrade, setShowTransferUpgrade] = useState(false);
  const { canAccess } = useSubscription();
  const transferLocked = !canAccess('transfers_access');

  // Populate form when editing or reset when creating new
  useEffect(() => {
    if (open) {
      setIsInitialLoad(true);
      if (transaction) {
        // Editing mode: populate with transaction data
        setAmount(String(Math.abs(transaction.amount)));
        setDescription(transaction.description);
        setCategoryId(transaction.category_id || "");
        setCategoryName(transaction.category?.name || "");
        setDate(transaction.transaction_date);
        setIsRecurring(false);
        setRecurrenceType("installment");
        setInstallments("2");
        setTransferPairId(null);

        const isTransfer =
          transaction.category_id === TRANSFER_EXPENSE_CAT_ID ||
          transaction.category_id === TRANSFER_INCOME_CAT_ID;

        if (isTransfer) {
          setType('transfer');

          let active = true;

          if (transaction.category_id === TRANSFER_EXPENSE_CAT_ID) {
            // This is the expense (origin) side
            setAccountId(transaction.account_id);
            (async () => {
              const { data: pair } = await supabase
                .from('transactions')
                .select('id, account_id')
                .eq('description', transaction.description)
                .eq('transaction_date', transaction.transaction_date)
                .eq('category_id', TRANSFER_INCOME_CAT_ID)
                .maybeSingle();
              if (active && pair) {
                setDestinationAccountId(pair.account_id);
                setTransferPairId(pair.id);
              }
            })();
          } else {
            // This is the income (destination) side — find the expense pair
            setDestinationAccountId(transaction.account_id);
            (async () => {
              const { data: pair } = await supabase
                .from('transactions')
                .select('id, account_id')
                .eq('description', transaction.description)
                .eq('transaction_date', transaction.transaction_date)
                .eq('category_id', TRANSFER_EXPENSE_CAT_ID)
                .maybeSingle();
              if (active && pair) {
                setAccountId(pair.account_id);
                setTransferPairId(pair.id);
              }
            })();
          }

          setTimeout(() => setIsInitialLoad(false), 100);
          return () => { active = false; };
        } else {
          setType(transaction.type as 'income' | 'expense');
          setAccountId(transaction.account_id);
        }
      } else {
        // Creating mode: reset form
        setType("expense");
        setAmount("");
        setDescription("");
        setCategoryId("");
        setCategoryName("");
        setDate(format(new Date(), "yyyy-MM-dd"));
        setIsRecurring(false);
        setRecurrenceType("installment");
        setInstallments("2");
        setDestinationAccountId("");
        setAccountId("");
        setTransferPairId(null);
        // accountId will be set by fetchAccounts
      }
      // Allow category reset only after initial load completes
      setTimeout(() => setIsInitialLoad(false), 100);
    }
  }, [open, transaction]);

  useEffect(() => {
    if (open) {
      fetchAccounts();
    }
  }, [open]);

  // Reset category when type changes (but not during initial load, and skip for transfer)
  useEffect(() => {
    if (!isInitialLoad && type !== "transfer") {
      setCategoryId("");
      setCategoryName("");
    }
  }, [type, isInitialLoad]);

  const fetchAccounts = async () => {
    const { data } = await supabase
      .from("accounts")
      .select("*")
      .eq("is_active", true)
      .eq("archived", false)
      .order("name");
    setAccounts(data || []);
    if (data && data.length > 0) {
      // When editing, keep the transaction's account; when creating, use defaultAccountId or first
      const editingAccountId = transaction?.account_id;
      const targetAccountId = editingAccountId && data.find(a => a.id === editingAccountId)
        ? editingAccountId
        : defaultAccountId && data.find(a => a.id === defaultAccountId)
          ? defaultAccountId
          : accountId && data.find(a => a.id === accountId)
            ? accountId
            : data[0].id;
      setAccountId(targetAccountId);

      // Set default destination to a different account
      if (data.length > 1) {
        const currentDest = destinationAccountId && data.find(a => a.id === destinationAccountId && a.id !== targetAccountId);
        if (!currentDest) {
          const dest = data.find(a => a.id !== targetAccountId);
          if (dest) setDestinationAccountId(dest.id);
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const transactionDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      transactionDate.setHours(0, 0, 0, 0);

      // Block transfer submit if locked
      if (type === "transfer" && transferLocked) {
        throw new Error("Transferências entre contas não estão disponíveis no seu plano atual.");
      }

      // Handle transfer between accounts
      if (type === "transfer") {
        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
          throw new Error("O valor deve ser maior que zero");
        }
        if (!destinationAccountId) {
          throw new Error("Selecione a conta de destino");
        }
        if (accountId === destinationAccountId) {
          throw new Error("A conta de origem e destino devem ser diferentes");
        }

        const isPaid = transactionDate <= today;
        const desc = description || "Transferência entre contas";

        if (isEditing && transaction && transferPairId) {
          // UPDATE the two existing transactions
          const isCurrentExpense = transaction.category_id === TRANSFER_EXPENSE_CAT_ID;
          const expenseId = isCurrentExpense ? transaction.id : transferPairId;
          const incomeId = isCurrentExpense ? transferPairId : transaction.id;

          const { error: expError } = await supabase.from("transactions").update({
            account_id: accountId,
            category_id: TRANSFER_EXPENSE_CAT_ID,
            amount: parsedAmount,
            type: "expense" as const,
            description: desc,
            transaction_date: date,
            is_paid: isPaid,
          }).eq("id", expenseId);
          if (expError) throw expError;

          const { error: incError } = await supabase.from("transactions").update({
            account_id: destinationAccountId,
            category_id: TRANSFER_INCOME_CAT_ID,
            amount: parsedAmount,
            type: "income" as const,
            description: desc,
            transaction_date: date,
            is_paid: isPaid,
          }).eq("id", incomeId);
          if (incError) throw incError;

          toast({
            title: "Transferência atualizada!",
            description: `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parsedAmount)} atualizado com sucesso.`,
          });

          onSuccess();
          handleClose();
          return;
        }

        // Fixed UUIDs for system transfer categories (set by migration 20260211200000)
        const expenseCatId = "00000000-0000-0000-0000-000000000101";
        const incomeCatId = "00000000-0000-0000-0000-000000000102";

        // Create two transactions: expense on source, income on destination
        const { error: txError } = await supabase.from("transactions").insert([
          {
            user_id: user.id,
            account_id: accountId,
            category_id: expenseCatId,
            amount: parsedAmount,
            type: "expense" as const,
            description: desc,
            transaction_date: date,
            is_paid: isPaid,
          },
          {
            user_id: user.id,
            account_id: destinationAccountId,
            category_id: incomeCatId,
            amount: parsedAmount,
            type: "income" as const,
            description: desc,
            transaction_date: date,
            is_paid: isPaid,
          },
        ]);

        if (txError) throw txError;

        toast({
          title: "Transferência realizada!",
          description: `${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parsedAmount)} transferido com sucesso.`,
        });

        onSuccess();
        handleClose();
        return;
      }

      if (isRecurring) {
        // Create recurring transactions
        const numInstallments = recurrenceType === "installment" ? parseInt(installments) : 12;
        const installmentAmount = recurrenceType === "installment" 
          ? parseFloat(amount) / numInstallments 
          : parseFloat(amount);

        const transactions = [];
        let currentDate = new Date(date);

        for (let i = 0; i < numInstallments; i++) {
          const txDate = new Date(currentDate);
          txDate.setHours(0, 0, 0, 0);
          const isPaid = txDate <= today;

          const descWithInstallment = recurrenceType === "installment"
            ? `${description || "Sem descrição"} (${i + 1}/${numInstallments})`
            : description || "Sem descrição";

          transactions.push({
            user_id: user.id,
            account_id: accountId,
            category_id: categoryId || null,
            amount: installmentAmount,
            type,
            description: descWithInstallment,
            transaction_date: format(currentDate, "yyyy-MM-dd"),
            is_paid: isPaid,
            is_recurring: true,
            is_fixed: recurrenceType === "fixed",
          });

          // Calculate next date
          if (frequency === "weekly") {
            currentDate = addWeeks(currentDate, 1);
          } else if (frequency === "monthly") {
            currentDate = addMonths(currentDate, 1);
          } else {
            currentDate = addYears(currentDate, 1);
          }
        }

        const { data: insertedTransactions, error: txError } = await supabase
          .from("transactions")
          .insert(transactions)
          .select();

        if (txError) throw txError;

        // Create recurrence record linked to first transaction
        const firstTransaction = insertedTransactions?.[0];
        if (firstTransaction) {
          const { error: recError } = await supabase.from("recurrences").insert({
            user_id: user.id,
            transaction_id: firstTransaction.id,
            frequency,
            interval: 1,
            start_date: date,
            end_date: null,
            occurrences: recurrenceType === "installment" ? numInstallments : null,
          });

          if (recError) throw recError;
        }

        toast({
          title: "Transação recorrente criada!",
          description: `${transactions.length} lançamentos gerados com sucesso.`,
        });
      } else {
        // Create or update single transaction
        const isPaid = transactionDate <= today;
        const parsedAmount = parseFloat(amount);
        
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
          throw new Error("O valor deve ser maior que zero");
        }

        if (isEditing && transaction) {
          // Update existing transaction
          const updateData = {
            account_id: accountId,
            category_id: categoryId || null,
            amount: parsedAmount,
            type,
            description: description || "Sem descrição",
            transaction_date: date,
            is_paid: isPaid,
          };
          
          console.log('Updating transaction:', transaction.id, updateData);
          
          const { error } = await supabase
            .from("transactions")
            .update(updateData)
            .eq("id", transaction.id);

          if (error) {
            console.error('Update error:', error);
            throw error;
          }

          toast({
            title: "Transação atualizada!",
            description: `${type === "income" ? "Receita" : "Despesa"} atualizada com sucesso.`,
          });
        } else {
          // Create new transaction
          const { error } = await supabase.from("transactions").insert({
            user_id: user.id,
            account_id: accountId,
            category_id: categoryId || null,
            amount: parsedAmount,
            type,
            description: description || "Sem descrição",
            transaction_date: date,
            is_paid: isPaid,
          });

          if (error) throw error;

          toast({
            title: "Transação criada!",
            description: `${type === "income" ? "Receita" : "Despesa"} adicionada com sucesso.`,
          });
        }
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: isEditing ? "Erro ao atualizar transação" : "Erro ao criar transação",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!isEditing) {
      setType("expense");
      setAmount("");
      setDescription("");
      setCategoryId("");
      setCategoryName("");
      setDate(format(new Date(), "yyyy-MM-dd"));
      setIsRecurring(false);
      setRecurrenceType("installment");
      setInstallments("2");
      setFrequency("monthly");
      setDestinationAccountId("");
    }
    onOpenChange(false);
  };

  // Calculate installment preview
  const installmentPreview = isRecurring && recurrenceType === "installment" && amount && installments
    ? parseFloat(amount) / parseInt(installments)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar Transação" : "Nova Transação"}</DialogTitle>
        </DialogHeader>

        <Tabs value={type} onValueChange={(v) => {
          if (v === 'transfer' && transferLocked) {
            setShowTransferUpgrade(true);
            return;
          }
          setType(v as any);
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="expense" className="gap-1.5 text-xs sm:text-sm">
              <TrendingDown className="h-4 w-4" />
              Despesa
            </TabsTrigger>
            <TabsTrigger value="income" className="gap-1.5 text-xs sm:text-sm">
              <TrendingUp className="h-4 w-4" />
              Receita
            </TabsTrigger>
            {(!isEditing || type === 'transfer') && (
              <TabsTrigger value="transfer" className="gap-1.5 text-xs sm:text-sm">
                {transferLocked ? (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ArrowLeftRight className="h-4 w-4" />
                )}
                Transfer.
              </TabsTrigger>
            )}
          </TabsList>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="amount">
                {isRecurring && recurrenceType === "installment" ? "Valor Total *" : "Valor *"}
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                autoFocus
              />
              {isRecurring && recurrenceType === "installment" && installmentPreview > 0 && (
                <p className="text-xs text-muted-foreground">
                  {installments}x de {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(installmentPreview)}
                </p>
              )}
            </div>

            {type !== "transfer" && (
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <CategorySelect
                value={categoryId || null}
                onChange={(id, name) => {
                  setCategoryId(id);
                  setCategoryName(name);
                }}
                type={type as "income" | "expense"}
                placeholder="Selecione a categoria"
                allowCreate={true}
              />
            </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                placeholder={type === "transfer" ? "Ex: Transferência para poupança" : "Ex: Supermercado"}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account">{type === "transfer" ? "Conta de Origem *" : "Conta *"}</Label>
              <Select value={accountId} onValueChange={setAccountId} required>
                <SelectTrigger id="account">
                  <SelectValue placeholder="Selecione a conta" />
                </SelectTrigger>
                <SelectContent>
                  {type === "transfer" ? (
                    (['checking', 'savings'] as const).map(accType => {
                      const group = accounts.filter(a => a.type === accType && a.id !== destinationAccountId);
                      if (group.length === 0) return null;
                      return (
                        <SelectGroup key={accType}>
                          <SelectLabel>{accType === 'checking' ? 'Contas Corrente' : 'Cofrinhos'}</SelectLabel>
                          {group.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id} className="pl-10">{acc.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      );
                    })
                  ) : (
                    (['checking', 'savings', 'credit_card'] as const).map(accType => {
                      const group = accounts.filter(a => a.type === accType);
                      if (group.length === 0) return null;
                      const label = accType === 'checking' ? 'Contas Corrente' : accType === 'savings' ? 'Cofrinhos' : 'Cartões';
                      return (
                        <SelectGroup key={accType}>
                          <SelectLabel>{label}</SelectLabel>
                          {group.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id} className="pl-10">{acc.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            {type === "transfer" && (
              <div className="space-y-2">
                <Label htmlFor="destination-account">Conta de Destino *</Label>
                <Select value={destinationAccountId} onValueChange={setDestinationAccountId} required>
                  <SelectTrigger id="destination-account">
                    <SelectValue placeholder="Selecione a conta de destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {(['checking', 'savings'] as const).map(accType => {
                      const group = accounts.filter(a => a.type === accType && a.id !== accountId);
                      if (group.length === 0) return null;
                      return (
                        <SelectGroup key={accType}>
                          <SelectLabel>{accType === 'checking' ? 'Contas Corrente' : 'Cofrinhos'}</SelectLabel>
                          {group.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id} className="pl-10">{acc.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="date">Data *</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>

            {/* Recurrence Section - Only show when creating and not transfer */}
            {!isEditing && type !== "transfer" && (
            <div className="space-y-3 p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="recurring" className="cursor-pointer">
                    Transação Recorrente
                  </Label>
                </div>
                <Switch
                  id="recurring"
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                />
              </div>

              {isRecurring && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={recurrenceType === "installment" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setRecurrenceType("installment")}
                    >
                      Parcelada
                    </Button>
                    <Button
                      type="button"
                      variant={recurrenceType === "fixed" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setRecurrenceType("fixed")}
                    >
                      Fixa
                    </Button>
                  </div>

                  {recurrenceType === "installment" && (
                    <div className="space-y-2">
                      <Label htmlFor="installments">Número de Parcelas</Label>
                      <Select value={installments} onValueChange={setInstallments}>
                        <SelectTrigger id="installments">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 18, 24, 36, 48].map((n) => (
                            <SelectItem key={n} value={n.toString()}>
                              {n}x
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequência</Label>
                    <Select value={frequency} onValueChange={(v) => setFrequency(v as any)}>
                      <SelectTrigger id="frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Semanal</SelectItem>
                        <SelectItem value="monthly">Mensal</SelectItem>
                        <SelectItem value="yearly">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </div>
          </form>
        </Tabs>

        {/* Transfer Upgrade Dialog */}
        {showTransferUpgrade && (
          <div className="mt-4">
            <UpgradePrompt feature="transfers_access" />
            <Button
              variant="ghost"
              className="w-full mt-2"
              onClick={() => setShowTransferUpgrade(false)}
            >
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
